/**
 * The questions a clock can ask, drawn above the whole shell.
 *
 * Mounted by `TimerProvider` rather than by the buttons that raise them, and
 * that placement is the point: half the ways to start or stop a timer sit inside
 * a row menu that closes on the very click that asks the question. A dialog
 * owned by that button would unmount with it and the operator would see a flash
 * and nothing else.
 *
 * Two of the four are confirmations and two are forms: the stale stop
 * (`TimerStopModal`), which decides a number, and the start (`TimerStartModal`),
 * which decides a task.
 */
import { useT } from "../../i18n/I18nProvider";
import { ConfirmModal } from "../sections/_shared/ConfirmModal";
import { TimerStartModal } from "./TimerStartModal";
import { TimerStopModal } from "./TimerStopModal";
import { useTimers } from "./TimerProvider";

export function TimerDialogs() {
  const t = useT();
  const timers = useTimers();
  const pending = timers?.pending ?? null;
  if (!timers || !pending) return null;

  if (pending.kind === "start") return <TimerStartModal />;

  if (pending.kind === "stop") {
    // Keyed on the clock, so asking about a SECOND one straight after the first
    // (the switch dialog can escalate into this) remounts the form instead of
    // reusing it. The hours field is frozen at mount by design; without the key
    // the second clock would open with the first one's figure in it.
    return <TimerStopModal key={pending.timer.id} timer={pending.timer} startAfter={pending.startAfter} />;
  }

  if (pending.kind === "switch") {
    const runningTitle = pending.running.task?.title ?? t("timer.unknownTask");
    return (
      <ConfirmModal
        title={t("timer.switchTitle")}
        body={t("timer.switchBody", { task: runningTitle })}
        confirmLabel={t("timer.switchConfirm")}
        cancelLabel={t("common.cancel")}
        /* Dark, not red. Nothing is destroyed here: the clock that is in the way
           gets STOPPED, which records its hours exactly as pressing stop would.
           Red would teach the operator that the colour means nothing. */
        tone="dark"
        onConfirm={async () => {
          const out = await timers.stopNow(pending.running);
          if (out.ok) {
            await timers.startNow(pending.intent.taskId, pending.intent.note);
            timers.closePending();
            return;
          }
          // The clock in the way is itself too old to stop in one click. Ask
          // about it, carrying the task the operator actually pressed so that
          // answering the hard question still ends where they were going.
          if (out.stale) {
            timers.askStop({ ...pending.running, stale: true }, pending.intent);
            return;
          }
          timers.closePending();
        }}
        onClose={timers.closePending}
      />
    );
  }

  return (
    <ConfirmModal
      title={t("timer.discardTitle")}
      body={t("timer.discardBody")}
      confirmLabel={t("timer.discard")}
      cancelLabel={t("common.cancel")}
      onConfirm={async () => {
        await timers.discardNow(pending.timer);
        timers.closePending();
      }}
      onClose={timers.closePending}
    />
  );
}
